import React, { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import {
  Paper,
  styled,
  Typography,
  useTheme
} from "@mui/material"

import {
  useNavigate,
  useParams
} from "react-router-dom"

import Cards from "../components/Cards/Cards"
import Columns from "../components/Columns"
import InPageNav from "../components/InPageNav"
import List from "../components/List"
import SubNav from "../components/SubNav"

import RichText from "../components/StyledComponents/RichText"

import HKVMarkdown from "../components/HKVMarkdown/4.0"

import { GetTheme, GetThemes } from "../store/reducers/functions"

import { groupByCategoryKey, isValidHttpUrl, scrollToElement } from "../lib/utils"

const StyledImage = styled("img", {
})(({ theme }) => {
  return {
    background: theme.palette.lichtblauw.main,
    borderTop: 0,
    borderRight: 1,
    borderBottom: 1,
    borderLeft: 1,
    borderColor: theme.palette.grey[300],
    borderStyle: "solid",
    width: "100%",
    minWidth: "100%",
    objectFit: "cover",
    [theme.breakpoints.down(1600)]: {
      borderRight: 0,
      borderLeft: 0
    }
  }
})

const StyledHero = styled("div", {
})(({ theme }) => {
  return {
    minHeight: 420,
    [theme.breakpoints.down("lg")]: {
      minHeight: 320
    },
    [theme.breakpoints.down("md")]: {
      minHeight: 220
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 150
    },
    margin: "0 auto",
    maxWidth: 1600,
    "& img": {
      minHeight: 420,
      height: 420,
      [theme.breakpoints.down("lg")]: {
        minHeight: 320,
        height: 320
      },
      [theme.breakpoints.down("md")]: {
        minHeight: 220,
        height: 220
      },
      [theme.breakpoints.down("sm")]: {
        minHeight: 150,
        height: 150
      }
    },
    "& .landing-hero-body": {
      position: "relative",
    }
  }
})

const StyledHeroBody = styled(Paper, {
})(({ theme }) => {
  return {
    maxWidth: 1200,
    minHeight: 164,
    margin: "0 auto",
    "& .section-body": {
      marginBottom: 0
    },
    [theme.breakpoints.down("lg")]: {
      margin: theme.spacing(0, 2),
    },
    [theme.breakpoints.down("md")]: {
      minHeight: 100
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 90
    }
  }
})

const StyledHeroTitle = styled(Typography, {
})(({ theme }) => {
  return {
    color: "white",
    textShadow: "0 0 4px #000c",
    fontSize: theme.typography.pxToRem(72),
    fontWeight: 400,
    lineHeight: 1.3,
    margin: `0 auto ${theme.spacing(2)}`,
    maxWidth: 1200,
    padding: theme.spacing(10, 2, 0),
    position: "relative",
    marginTop: -320,

    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",

    [theme.breakpoints.down("md")]: {
      fontSize: theme.typography.pxToRem(52),
      marginTop: -220,
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 2, 0),
      fontSize: theme.typography.pxToRem(40),
      marginTop: -150,
    }
  }
})

const Thema = (props) => {
  const dispatch = useDispatch()

  const theme = useTheme()

  const navigate = useNavigate()
  const params = useParams()

  const {
    categories = []
  } = props

  const [details, setDetails] = useState()

  useEffect(() => {
    const hash = decodeURIComponent(window.location.hash.slice(1))
    if (details && hash) {
      setTimeout(() => {
        scrollToElement(hash, -16, "smooth")
      }, 100)
    }
  }, [details])

  const getThemeDetails = async () => {
    if (Object.keys(params).length === 0) {
      dispatch(GetThemes())
        .then((json) => {
          const data = Array.isArray(json) ? json : []
          const groupedThemes = groupByCategoryKey(data)

          setDetails({
            name: "test",
            description: "test",
            image_url: "test",
            themes: groupedThemes
          })
        })

      return
    }

    if (params.hasOwnProperty("thema")) {
      if (params.hasOwnProperty("item")) {
        const themesData = await dispatch(GetThemes())
        const themeData = await dispatch(GetTheme(params.item))

        if (themesData && themeData) {
          setDetails({
            ...themeData,
            image_url: isValidHttpUrl(themeData.image_url) ? themeData.image_url : `/static/images/theme_images/${themeData.image_url}`,
            themes: themesData
          })
        } else {
          navigate(`/${params.item}`)
        }
      } else {
        dispatch(GetThemes({ theme: params.thema }))
          .then((json) => {
            const data = Array.isArray(json) ? json : []
            if (data.length > 0) {
              setDetails({
                name: data[0].category.name,
                description: data[0].category.description,
                image_url: isValidHttpUrl(data[0].image_url) ? data[0].image_url : `/static/images/theme_images/${data[0].image_url}`,
                themes: data
              })
            } else {
              navigate(`/${params.thema}`)
            }
          })
      }
    }
  }

  useEffect(() => {
    getThemeDetails()
  }, [params.thema, params.item])

  useEffect(() => {
    let lastHighlightedId = null

    const handleScroll = () => {
      const offsets = details?.questions?.map((q) => {
        const id = q.question?.trim()?.replaceAll(" ", "_")?.replaceAll("?", "")?.toLowerCase()
        const el = document.getElementById(id)
        if (!el) return null

        const rect = el.getBoundingClientRect()
        return {
          id,
          top: rect.top,
        }
      }).filter(Boolean)

      const visible = offsets
        ?.filter(o => o.top < window.innerHeight / 2 && o.top > -200)
        ?.sort((a, b) => b.top - a.top)?.[0]

      if (visible?.id && visible.id !== lastHighlightedId) {
        // Remove highlight from previous item
        if (lastHighlightedId) {
          const prevNavItem = document.getElementById(`${lastHighlightedId}_inpagenav`)
          if (prevNavItem) {
            prevNavItem.style.borderColor = theme.palette.grey[300]
            const typography = prevNavItem.querySelector("p")
            if (typography) {
              typography.style.color = theme.palette.grey[600]
            }
          }
        }

        // Highlight the current item
        const currentNavItem = document.getElementById(`${visible.id}_inpagenav`)
        if (currentNavItem) {
          currentNavItem.style.borderColor = theme.palette.hemelblauw.main
          const typography = currentNavItem.querySelector("p")
          if (typography) {
            typography.style.color = theme.palette.text.primary
          }
        }

        lastHighlightedId = visible.id
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener("scroll", handleScroll)
  }, [details?.questions])

  const createThemeLink = useCallback((card) => card?.key ? `/themas/${card.category.key}/${card.key}` : "themas", [])

  if (!details) return <div className="page-landing" />

  const renderOtherThemes = () => {
    const otherThemes = details?.themes?.filter(theme => theme.category.key === params.thema && theme.key !== params.item) || []

    if (otherThemes?.length === 0) return

    return (
      <React.Fragment>
        <section className="brick">
          <section className="section section-cards">
            <div className="section-head">
              <Typography variant="h2" color="lintblauw">Zie ook</Typography>
            </div>
            <Cards
              cards={otherThemes}
              columns={Math.min(otherThemes.length, 4)}
              createLink={createThemeLink}
              footer={false}
            />
          </section>
        </section>
        {/* <section className="brick">
          <section className="section-line"><hr /></section>
        </section> */}
      </React.Fragment>
    )
  }

  const renderOtherLinks = () => {
    const otherCategories = categories.filter(category => category?.key !== details.category?.key)
    const currentCategoryLinks = categories.find(category => category?.key === details.category?.key)?.links || []

    return (
      <section className="brick">
        <section className="section section-cards">
          <div className="section-head">
            <Typography variant="h2" color="lintblauw">Links</Typography>
          </div>
        </section>
        <div className="brick-body">
          <Columns columns={3}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <List
                // title="Lorem ipsum"
                // titleVariant="h3"
                items={currentCategoryLinks.filter((l, i) => !(i % 2))}
                variant="white"
                bordered
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <List
                // title="Lorem ipsum"
                // titleVariant="h3"
                items={currentCategoryLinks.filter((l, i) => (i % 2))}
                variant="white"
                bordered
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <List
                title="Direct naar"
                titleVariant="h3"
                items={otherCategories.map(category => (
                  {
                    name: category.name,
                    link: `/themas/${category.key}`
                  }
                ))}
                variant="yellow"
              />
            </div>
          </Columns>
        </div>
      </section>
    )
  }

  if (params.thema) {
    if (params.item) {
      return (
        <div className="page-landing">
          <StyledHero className="landing-hero">
            <StyledImage
              src={details.image_url}
              title={details.image_url}
              alt={details.image_url}
            />
            <StyledHeroTitle
              className="landing-hero-title"
              variant="h1"
            >
              {details.name}
            </StyledHeroTitle>
            <StyledHeroBody className="landing-hero-body">
              <section className="section section-rich-text">
                <div className="section-body">
                  <RichText>
                    <Typography className="lead">{details.description}</Typography>
                  </RichText>
                </div>
              </section>
            </StyledHeroBody>
          </StyledHero>
          <section className="brick" style={{ display: "flex", position: "relative" }}>
            <SubNav
              showCurrentPage={true}
            />
            <section className="section section-rich-text" style={{ paddingTop: 0 }}>
              <div style={{ maxWidth: 640 }}>
                {details.questions?.map((question, ix) => (
                  <div
                    key={ix}
                    id={question.question?.trim()?.replaceAll(" ", "_")?.replaceAll("?", "")?.toLowerCase()}
                    style={{ marginTop: ix === 0 ? 0 : theme.spacing(2) }}
                  >
                    <HKVMarkdown
                      source={question.content}
                      className="rich-text-full"
                    />
                  </div>
                ))}
              </div>
            </section>
            <InPageNav
              items={details.questions}
            />
          </section>
          <section className="brick">
            <section className="section-line"><hr /></section>
          </section>
          {renderOtherThemes()}
          {renderOtherLinks()}
        </div>
      )
    } else {
      return (
        <div className="page-landing">
          <StyledHero className="landing-hero">
            <StyledImage
              src={details.image_url}
              title={details.image_url}
              alt={details.image_url}
            />
            <StyledHeroTitle
              className="landing-hero-title"
              variant="h1"
            >
              {details.name}
            </StyledHeroTitle>
            <StyledHeroBody className="landing-hero-body">
              <section className="section section-rich-text">
                <div className="section-body">
                  <RichText>
                    <Typography className="lead">{details.description}</Typography>
                  </RichText>
                </div>
              </section>
            </StyledHeroBody>
          </StyledHero>
          <section className="brick">
            <section className="section section-cards">
              <Cards
                cards={details.themes}
                columns={3}
                createLink={createThemeLink}
                footer={false}
              />
            </section>
          </section>
        </div>
      )
    }
  } else {
    return (
      <div className="page-landing">
        {details?.themes?.map((th, ix) => (
          <section key={ix} className="brick">
            <section className="section section-cards">
              <div className="section-body">
                <Typography
                  variant="h1"
                  color="lintblauw"
                >
                  {th?.category?.name}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ marginBottom: theme.spacing(2), fontWeight: "normal" }}
                >
                  {th?.category?.description}
                </Typography>
                <Cards
                  cards={th?.items}
                  columns={3}
                  createLink={createThemeLink}
                  footer={false}
                />
              </div>
              {ix !== details.themes.length - 1 && <section className="brick">
                <section className="section-line"><hr /></section>
              </section>}
            </section>
          </section>
        ))}
      </div>
    )
  }
}

export default React.memo(Thema)